import Avatar from "@/components/Avatar";
import { getMyBusker } from "@/services/supabase-service";
import { useBuskerStore } from "@/store/busker";
import { Busker } from "@/types/type";
import { supabase } from "@/utils/supabase";
import { MaterialCommunityIcons } from "@expo/vector-icons";
import { Session } from "@supabase/supabase-js";
import { router } from "expo-router";
import { useEffect, useState } from "react";
import {
  Alert,
  Button,
  StyleSheet,
  Text,
  TextInput,
  TouchableOpacity,
  View,
} from "react-native";

export default function Account({ session }: { session: Session }) {
  const [loading, setLoading] = useState(true);
  const [buskerProfileExists, setBuskerProfileExists] = useState(false);
  const [busker, setBusker] = useState<Busker | null>(null);
  const [website, setWebsite] = useState("");
  const [avatarUrl, setAvatarUrl] = useState("");

  useEffect(() => {
    if (session) getProfile();

    const fetchBusker = async () => {
      const fetchedBusker = await getMyBusker(session?.user?.id);
      setBuskerProfileExists(!!fetchedBusker);
      setBusker(fetchedBusker);
    };

    fetchBusker();
  }, [session]);

  async function getProfile() {
    try {
      setLoading(true);
      if (!session?.user) throw new Error("No user on the session!");

      const { data, error, status } = await supabase
        .from("profiles")
        .select(`website, avatar_url`)
        .eq("id", session?.user.id)
        .single();
      if (error && status !== 406) {
        throw error;
      }

      if (data) {
        setWebsite(data.website);
        setAvatarUrl(data.avatar_url);
      }
    } catch (error) {
      if (error instanceof Error) {
        Alert.alert(error.message);
      }
    } finally {
      setLoading(false);
    }
  }

  async function updateProfile({
    website,
    avatar_url,
  }: {
    website: string;
    avatar_url: string;
  }) {
    try {
      setLoading(true);
      if (!session?.user) throw new Error("No user on the session!");

      const updates = {
        id: session?.user.id,
        website,
        avatar_url,
        updated_at: new Date(),
      };

      const { error } = await supabase.from("profiles").upsert(updates);

      if (error) {
        throw error;
      }
    } catch (error) {
      if (error instanceof Error) {
        Alert.alert(error.message);
      }
    } finally {
      setLoading(false);
    }
  }

  return (
    <View style={styles.container}>
      <View>
        <Avatar
          size={200}
          url={avatarUrl}
          onUpload={(url: string) => {
            setAvatarUrl(url);
            updateProfile({ website, avatar_url: url });
          }}
        />
      </View>
      <View style={[styles.verticallySpaced, styles.mt20]}>
        <Text>{session?.user?.email} </Text>
      </View>
      <View style={styles.verticallySpaced}>
        <TextInput
          value={website || ""}
          onChangeText={(text) => setWebsite(text)}
        />
      </View>

      <View style={[styles.verticallySpaced, styles.mt20]}>
        <Button
          title={loading ? "Loading ..." : "Update"}
          onPress={() => updateProfile({ website, avatar_url: avatarUrl })}
          disabled={loading}
        />
      </View>

      <View className="w-full">
        <TouchableOpacity
          className="w-[20px] h-[20px] rounded-lg flex-row items-center justify-center"
          onPress={async () => {
            router.push(`/busker-create?busker_id=${busker.id}`);
          }}
        >
          <MaterialCommunityIcons name="human-edit" size={24} />
        </TouchableOpacity>
      </View>

      <View style={styles.verticallySpaced}>
        <Button title="Sign Out" onPress={() => supabase.auth.signOut()} />
      </View>
    </View>
  );
}

const styles = StyleSheet.create({
  container: {
    marginTop: 40,
    padding: 12,
  },
  verticallySpaced: {
    paddingTop: 4,
    paddingBottom: 4,
    alignSelf: "stretch",
  },
  mt20: {
    marginTop: 20,
  },
});
